import { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Briefcase, Calendar, Trash2, Edit, Plus, Clock, CheckCircle, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["Sonoma", "Napa", "Marin", "Lake", "Mendocino"];
const jobTypes = ["Full-Time", "Part-Time", "Internship", "Apprenticeship"];
const eventTypes = ["Career Fair", "Volunteer", "Workshop", "Networking"];

export default function AdminPage() {
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: "",
    company: "",
    county: "Sonoma",
    city: "",
    job_type: "Full-Time",
    trade_type: "",
    description: "",
    skills: "",
    pay_range: "",
    apply_info: ""
  });
  const [eventForm, setEventForm] = useState({
    name: "",
    county: "Sonoma",
    city: "",
    event_type: "Career Fair",
    date: "",
    time: "",
    description: "",
    location: "",
    signup_info: ""
  });
  const [pendingJobs, setPendingJobs] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchEvents();
    fetchPendingJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error("Failed to fetch jobs", error);
    }
  };

  const fetchPendingJobs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/jobs/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingJobs(response.data);
    } catch (error) {
      console.error("Failed to fetch pending jobs", error);
    }
  };

  const handleApproveJob = async (jobId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/jobs/${jobId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job approved and published");
      fetchPendingJobs();
      fetchJobs();
    } catch (error) {
      toast.error("Failed to approve job");
    }
  };

  const handleRejectJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to reject this job posting?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/jobs/${jobId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job rejected");
      fetchPendingJobs();
    } catch (error) {
      toast.error("Failed to reject job");
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch events", error);
    }
  };

  const handleCreateJob = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.id}`, jobForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Job updated successfully");
      } else {
        await axios.post(`${API}/jobs`, jobForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Job created successfully");
      }
      setJobDialogOpen(false);
      setEditingJob(null);
      setJobForm({
        title: "",
        company: "",
        county: "Sonoma",
        city: "",
        job_type: "Full-Time",
        trade_type: "",
        description: "",
        skills: "",
        pay_range: "",
        apply_info: ""
      });
      fetchJobs();
    } catch (error) {
      toast.error("Failed to save job");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to deactivate this job?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job deactivated");
      fetchJobs();
    } catch (error) {
      toast.error("Failed to deactivate job");
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title,
      company: job.company,
      county: job.county,
      city: job.city,
      job_type: job.job_type,
      trade_type: job.trade_type,
      description: job.description,
      skills: job.skills,
      pay_range: job.pay_range || "",
      apply_info: job.apply_info
    });
    setJobDialogOpen(true);
  };

  const handleCreateEvent = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event updated successfully");
      } else {
        await axios.post(`${API}/events`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event created successfully");
      }
      setEventDialogOpen(false);
      setEditingEvent(null);
      setEventForm({
        name: "",
        county: "Sonoma",
        city: "",
        event_type: "Career Fair",
        date: "",
        time: "",
        description: "",
        location: "",
        signup_info: ""
      });
      fetchEvents();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to deactivate this event?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event deactivated");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to deactivate event");
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      county: event.county,
      city: event.city,
      event_type: event.event_type,
      date: event.date,
      time: event.time,
      description: event.description,
      location: event.location,
      signup_info: event.signup_info
    });
    setEventDialogOpen(true);
  };

  const handleSyncJobs = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/sync-jobs`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const stats = response.data.stats || {};
      toast.success(`Synced ${stats.inserted || 0} new jobs, ${stats.updated || 0} updated from Indeed`);
      fetchJobs();
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error("Failed to sync jobs from Indeed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk', color: '#FF6B6B' }}>
              Admin Dashboard
            </h1>
            <p className="text-gray-600">Manage job postings and events</p>
          </div>
          <Button
            onClick={handleSyncJobs}
            disabled={syncing}
            className="flex items-center gap-2"
            data-testid="sync-jobs-button"
            style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
          >
            <svg className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {syncing ? 'Syncing...' : 'Sync Indeed Jobs'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" data-testid="pending-tab">Pending Approval</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="jobs-tab">Job Postings</TabsTrigger>
          <TabsTrigger value="events" data-testid="events-tab">Events</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader>
              <CardTitle className="text-lg">Pending Employer Submissions</CardTitle>
              <CardDescription>Review and approve job postings submitted by employers</CardDescription>
            </CardHeader>
          </Card>

          {pendingJobs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No pending job submissions</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingJobs.map((job) => (
                <Card key={job.id} className="border-amber-300" data-testid={`pending-job-card-${job.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">{job.title}</CardTitle>
                        <CardDescription>{job.company} - {job.city}, {job.county}</CardDescription>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700">Pending Review</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Description:</p>
                      <p className="text-sm text-gray-600">{job.description}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Required Skills:</p>
                      <p className="text-sm text-gray-600">{job.skills}</p>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline">{job.job_type}</Badge>
                      <Badge variant="outline">{job.trade_type}</Badge>
                      {job.pay_range && <Badge variant="outline">{job.pay_range}</Badge>}
                    </div>
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        onClick={() => handleApproveJob(job.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid={`approve-job-${job.id}`}
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approve & Publish
                      </Button>
                      <Button
                        onClick={() => handleRejectJob(job.id)}
                        variant="destructive"
                        className="flex-1"
                        data-testid={`reject-job-${job.id}`}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="w-full sm:w-auto"
                data-testid="create-job-button"
                style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                onClick={() => {
                  setEditingJob(null);
                  setJobForm({
                    title: "",
                    company: "",
                    county: "Sonoma",
                    city: "",
                    job_type: "Full-Time",
                    trade_type: "",
                    description: "",
                    skills: "",
                    pay_range: "",
                    apply_info: ""
                  });
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingJob ? "Edit Job" : "Create New Job"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Title*</Label>
                    <Input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} data-testid="job-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Company*</Label>
                    <Input value={jobForm.company} onChange={(e) => setJobForm({ ...jobForm, company: e.target.value })} data-testid="job-company-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>County*</Label>
                    <Select value={jobForm.county} onValueChange={(value) => setJobForm({ ...jobForm, county: value })}>
                      <SelectTrigger data-testid="job-county-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>City*</Label>
                    <Input value={jobForm.city} onChange={(e) => setJobForm({ ...jobForm, city: e.target.value })} data-testid="job-city-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Type*</Label>
                    <Select value={jobForm.job_type} onValueChange={(value) => setJobForm({ ...jobForm, job_type: value })}>
                      <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{jobTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Trade Type*</Label>
                    <Input value={jobForm.trade_type} onChange={(e) => setJobForm({ ...jobForm, trade_type: e.target.value })} placeholder="e.g., Electrical" data-testid="job-trade-type-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Pay Range</Label>
                  <Input value={jobForm.pay_range} onChange={(e) => setJobForm({ ...jobForm, pay_range: e.target.value })} placeholder="e.g., $20-25/hr" data-testid="job-pay-range-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description*</Label>
                  <Textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} rows={4} data-testid="job-description-input" />
                </div>
                <div className="space-y-2">
                  <Label>Required Skills*</Label>
                  <Textarea value={jobForm.skills} onChange={(e) => setJobForm({ ...jobForm, skills: e.target.value })} rows={3} data-testid="job-skills-input" />
                </div>
                <div className="space-y-2">
                  <Label>How to Apply*</Label>
                  <Textarea value={jobForm.apply_info} onChange={(e) => setJobForm({ ...jobForm, apply_info: e.target.value })} rows={2} placeholder="Email, phone, or website" data-testid="job-apply-info-input" />
                </div>
                <Button onClick={handleCreateJob} className="w-full" data-testid="save-job-button" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}>
                  {editingJob ? "Update Job" : "Create Job"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="grid gap-4">
            {jobs.map((job) => (
              <Card key={job.id} data-testid={`admin-job-card-${job.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      <CardDescription>{job.company} - {job.city}, {job.county}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditJob(job)} data-testid={`edit-job-${job.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteJob(job.id)} data-testid={`delete-job-${job.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Badge>{job.job_type}</Badge>
                    <Badge variant="outline">{job.trade_type}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="w-full sm:w-auto"
                data-testid="create-event-button"
                style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                onClick={() => {
                  setEditingEvent(null);
                  setEventForm({
                    name: "",
                    county: "Sonoma",
                    city: "",
                    event_type: "Career Fair",
                    date: "",
                    time: "",
                    description: "",
                    location: "",
                    signup_info: ""
                  });
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEvent ? "Edit Event" : "Create New Event"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Event Name*</Label>
                  <Input value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} data-testid="event-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>County*</Label>
                    <Select value={eventForm.county} onValueChange={(value) => setEventForm({ ...eventForm, county: value })}>
                      <SelectTrigger data-testid="event-county-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>City*</Label>
                    <Input value={eventForm.city} onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })} data-testid="event-city-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Event Type*</Label>
                  <Select value={eventForm.event_type} onValueChange={(value) => setEventForm({ ...eventForm, event_type: value })}>
                    <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{eventTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date*</Label>
                    <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Time*</Label>
                    <Input value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} placeholder="e.g., 10:00 AM - 2:00 PM" data-testid="event-time-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Location*</Label>
                  <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Full address" data-testid="event-location-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description*</Label>
                  <Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} rows={4} data-testid="event-description-input" />
                </div>
                <div className="space-y-2">
                  <Label>Sign Up Info*</Label>
                  <Textarea value={eventForm.signup_info} onChange={(e) => setEventForm({ ...eventForm, signup_info: e.target.value })} rows={2} placeholder="How to register" data-testid="event-signup-info-input" />
                </div>
                <Button onClick={handleCreateEvent} className="w-full" data-testid="save-event-button" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}>
                  {editingEvent ? "Update Event" : "Create Event"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="grid gap-4">
            {events.map((event) => (
              <Card key={event.id} data-testid={`admin-event-card-${event.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{event.name}</CardTitle>
                      <CardDescription>{event.city}, {event.county} - {event.date}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditEvent(event)} data-testid={`edit-event-${event.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteEvent(event.id)} data-testid={`delete-event-${event.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Badge>{event.event_type}</Badge>
                    <Badge variant="outline">{event.time}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}